import { paywallCheckoutApi } from "@circle-react/api/paywallCheckoutApi";
import { useCallback } from "react";

import type { VerificationsCreateParams } from "@/react/api/paywallCheckoutApi.types";
import { isCheckoutAttemptProcessingOrWaitingForConfirmation } from "@/react/helpers/checkoutAttemptHelpers";
import type { CheckoutAttemptStatus } from "@/react/types/PaywallCheckoutAttempt";

import { CheckoutError } from "../../CheckoutError";
import { processWithRetries } from "../../hooks/paymentMethods/helpers";
import { clearExternalTransactionToken } from "../helpers/externalTransactionToken";
import type { OnCheckoutTermsChanged, OnStageChange } from "../types";
import { stages } from "../types";

const resetURL = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete("forwarded");
  url.searchParams.delete("invoice");
  url.searchParams.delete("payment_intent_client_secret");
  url.searchParams.delete("payment_intent");
  url.searchParams.delete("payment_method_type");
  url.searchParams.delete("redirect_status");
  url.searchParams.delete("setup_intent");
  url.searchParams.delete("setup_intent_client_secret");
  url.searchParams.delete("setup_intent_id");
  url.searchParams.delete("session_id");
  window.history.replaceState({}, "", url);
  // This is only reached on a terminal (non-retryable) verification failure
  // -- clear the Google Play ECL token so it never leaks into a later,
  // unrelated checkout in the same tab.
  clearExternalTransactionToken();
};

const prepareFailedVerification = async (
  error: CheckoutError,
  setupIntentId: string,
  onCheckoutTermsChanged?: OnCheckoutTermsChanged,
): Promise<void> => {
  if (!setupIntentId || error.isNewPaywallError()) {
    resetURL();
  }

  const isCheckoutTermsChanged =
    error.isNewPaywallError() &&
    error.context.failure.code === "checkout_terms_changed";
  if (!isCheckoutTermsChanged) {
    return;
  }
  if (!onCheckoutTermsChanged) {
    return;
  }

  try {
    await onCheckoutTermsChanged();
  } catch {
    // The original terms-changed failure remains the actionable checkout error.
  }
};

interface CreateVerificationResponse {
  attemptId: number;
  attemptStatus: CheckoutAttemptStatus;
  invoiceId?: string;
  paymentIntentId?: string;
  subscriptionId?: string;
  targetTrialDays?: number;
  userSignedIn?: boolean;
}
const createVerification = async (
  params: VerificationsCreateParams,
): Promise<CreateVerificationResponse> => {
  const verificationResponse =
    await paywallCheckoutApi.verifications.create(params);
  if (!verificationResponse || !verificationResponse.checkout_attempt_status) {
    throw CheckoutError.fromUnknownError(
      "useVerifyAttempt.createVerification",
      verificationResponse,
    );
  }
  return {
    attemptId: verificationResponse.checkout_attempt_id,
    attemptStatus: verificationResponse.checkout_attempt_status,
    targetTrialDays: verificationResponse.target_trial_days,
    invoiceId: verificationResponse.invoice_processor_id,
    paymentIntentId: verificationResponse.payment_intent_processor_id,
    subscriptionId: verificationResponse.subscription_processor_id,
    userSignedIn: verificationResponse.user_signed_in,
  };
};
const verifyAttemptStatus = async (
  params: VerificationsCreateParams,
): Promise<CreateVerificationResponse | null> => {
  const response = await createVerification(params);
  if (
    isCheckoutAttemptProcessingOrWaitingForConfirmation(response.attemptStatus)
  ) {
    return null;
  }
  return response;
};

export interface VerifyAttemptProps {
  invoiceId?: string;
  paymentIntentId?: string;
  paymentMethodType?: string;
  setupIntentId?: string;
}

export interface UseVerifyAttemptProps {
  canVerifyAttempt: () => boolean;
  onCheckoutTermsChanged?: OnCheckoutTermsChanged;
  onStageChange: OnStageChange;
  paywallId: number;
  sessionId?: number;
}

const buildVerificationParams = ({
  invoiceId,
  paymentIntentId,
  paymentMethodType,
  paywallId,
  sessionId,
  setupIntentId,
}: VerifyAttemptProps & {
  paywallId: number;
  sessionId?: number;
}): VerificationsCreateParams => {
  if (paymentIntentId) {
    return {
      payment_intent_id: paymentIntentId,
      payment_method_type: paymentMethodType,
    };
  }
  if (invoiceId) {
    return {
      invoice_id: invoiceId,
      payment_method_type: paymentMethodType,
    };
  }
  if (setupIntentId && sessionId) {
    return {
      paywall_id: paywallId,
      payment_method_type: paymentMethodType,
      session_id: sessionId,
      setup_intent_id: setupIntentId,
    };
  }

  throw CheckoutError.generic("useVerifyAttempt.buildVerificationParams");
};

export const useVerifyAttempt = ({
  canVerifyAttempt,
  onCheckoutTermsChanged,
  onStageChange,
  paywallId,
  sessionId,
}: UseVerifyAttemptProps) => {
  const verifyAttempt = useCallback(
    async ({
      invoiceId = "",
      paymentMethodType = "",
      paymentIntentId = "",
      setupIntentId = "",
    }: VerifyAttemptProps) => {
      if (!canVerifyAttempt()) {
        return;
      }

      // No invoice and payment intent? Nothing to verify.
      if (!invoiceId && !paymentIntentId && !setupIntentId) {
        return;
      }

      onStageChange({ stage: stages.verifyingAttempt });

      try {
        const verificationParams = buildVerificationParams({
          invoiceId,
          paymentIntentId,
          paymentMethodType,
          paywallId,
          sessionId,
          setupIntentId,
        });
        const executor = () => verifyAttemptStatus(verificationParams);
        const response = await processWithRetries<CreateVerificationResponse>({
          executor,
        });

        // No response? It means we exhausted all retries and the attempt is still not completed.
        if (!response) {
          if (setupIntentId) {
            return;
          }
          // TODO: better handle these cases to prevent members from unnecessarily repeating attempts.
          throw CheckoutError.generic("useVerifyAttempt.verifyAttempt");
        }

        onStageChange({
          attemptId: response.attemptId,
          attemptStatus: response.attemptStatus,
          targetTrialDays: response.targetTrialDays,
          invoiceId: response.invoiceId,
          paymentIntentId: response.paymentIntentId,
          paymentMethodType,
          stage: stages.completed,
          subscriptionId: response.subscriptionId,
          userSignedIn: response.userSignedIn,
        });
      } catch (e: unknown) {
        const error = CheckoutError.fromUnknownError(
          "useVerifyAttempt.verifyAttempt",
          e,
        );
        await prepareFailedVerification(
          error,
          setupIntentId,
          onCheckoutTermsChanged,
        );
        onStageChange({
          stage: stages.failed,
          error,
        });
      }
    },
    [
      canVerifyAttempt,
      onCheckoutTermsChanged,
      onStageChange,
      paywallId,
      sessionId,
    ],
  );

  return verifyAttempt;
};
